import { Alert, Button, Spinner } from '@heroui/react';
import './product-selection.css';
import { useOutletContext } from 'react-router-dom';
import { useEffect, useCallback } from 'react';
import { TLoggedInUserInfo } from '../../shared/types/loggedInUserInfo';
import { useSubscriptions } from '../auth/hooks/useSubscriptions';
import { addSeascapeLogActivity } from '../activity-logging/activityLogApi';
import { SeascapeActivityLogConstants } from '../activity-logging/activityLogTypes';
import { trackException, trackInfo, LogLevel } from '../../shared/services/appInsights';
import {
	AppInsightsActions,
	AppInsightsComponents,
	AppInsightsNavigationReasons,
} from '../../shared/services/appInsightsConstants';

const ProductSelection = () => {
	const loggedInUserInfo = useOutletContext<TLoggedInUserInfo>();
	const isMobile = window.matchMedia('(max-width: 768px)').matches;
	const { isSubscribedToSeascape, isSubscribedToHorizon } = useSubscriptions(loggedInUserInfo);

	// Safe navigation helper with error handling and tracking
	const navigateToProduct = useCallback(
		(productName: string, targetUrl: string, reason: string) => {
			try {
				// Track navigation attempt
				trackInfo(`Product navigation initiated - ${productName}`, {
					userId: loggedInUserInfo?.id || 'unknown',
					emailAddress: loggedInUserInfo?.emailAddress || 'unknown',
					component: AppInsightsComponents.PRODUCT_SELECTION,
					action: AppInsightsActions.PRODUCT_NAVIGATION,
					targetProduct: productName,
					targetUrl,
					reason,
					isMobile: isMobile.toString(),
					isSubscribedToSeascape: isSubscribedToSeascape.toString(),
					isSubscribedToHorizon: isSubscribedToHorizon.toString(),
				});

				// Validate URL exists
				if (!targetUrl) {
					throw new Error(`${productName} URL not configured in window.config`);
				}

				// Navigate to external product
				window.location.href = targetUrl;
			} catch (error) {
				// Track navigation failure
				trackException(error as Error, LogLevel.Error, {
					userId: loggedInUserInfo?.id || 'unknown',
					emailAddress: loggedInUserInfo?.emailAddress || 'unknown',
					component: AppInsightsComponents.PRODUCT_SELECTION,
					action: AppInsightsActions.NAVIGATION_FAILURE,
					targetProduct: productName,
					targetUrl: targetUrl || 'undefined',
					reason,
					errorMessage: (error as Error).message,
				});

				console.error(`Failed to navigate to ${productName}:`, error);

				// Could show user-friendly error message here
				// For now, we'll let the component continue to render normally
			}
		},
		[loggedInUserInfo?.id, loggedInUserInfo?.emailAddress, isMobile, isSubscribedToSeascape, isSubscribedToHorizon],
	);

	useEffect(() => {
		if (isSubscribedToSeascape && !isMobile && !isSubscribedToHorizon) {
			navigateToProduct(
				'Seascape',
				window.config?.SEASCAPE_APP_URL,
				AppInsightsNavigationReasons.SINGLE_PRODUCT_SEASCAPE_DESKTOP,
			);
		} else if ((isSubscribedToHorizon && !isSubscribedToSeascape) || (isSubscribedToHorizon && isMobile)) {
			navigateToProduct(
				'Horizon',
				`${window.config?.HORIZON_APP_URL}/dashboard`,
				AppInsightsNavigationReasons.SINGLE_PRODUCT_HORIZON_OR_MOBILE,
			);
		} else if (!isSubscribedToSeascape && !isSubscribedToHorizon) {
			// Track when user has no product subscriptions
			trackInfo('User has no product subscriptions', {
				userId: loggedInUserInfo?.id || 'unknown',
				emailAddress: loggedInUserInfo?.emailAddress || 'unknown',
				component: AppInsightsComponents.PRODUCT_SELECTION,
				action: AppInsightsActions.NO_SUBSCRIPTIONS,
				isSubscribedToSeascape: isSubscribedToSeascape.toString(),
				isSubscribedToHorizon: isSubscribedToHorizon.toString(),
			});
		}
	}, [
		isSubscribedToSeascape,
		isSubscribedToHorizon,
		isMobile,
		loggedInUserInfo?.id,
		loggedInUserInfo?.emailAddress,
		navigateToProduct,
	]);

	if (isSubscribedToHorizon && isSubscribedToSeascape && !isMobile) {
		return (
			<div className="productSelection">
				<div className="activity hidden md:block">
					<div className="innerFrame">
						<div className="msiLogo"></div>
						<div>
							<Button
								size="lg"
								className="seascapeButton w-52"
								onPress={() => {
									try {
										const payload = JSON.stringify({
											Product: 'Seascape',
										});
										const eventId = SeascapeActivityLogConstants.PRODUCT_CHOOSE;
										addSeascapeLogActivity({ eventId, payload });

										navigateToProduct(
											'Seascape',
											window.config?.SEASCAPE_APP_URL,
											AppInsightsNavigationReasons.MANUAL_SELECTION_SEASCAPE,
										);
									} catch (error) {
										trackException(error as Error, LogLevel.Error, {
											userId: loggedInUserInfo?.id || 'unknown',
											emailAddress: loggedInUserInfo?.emailAddress || 'unknown',
											component: AppInsightsComponents.PRODUCT_SELECTION,
											action: AppInsightsActions.SEASCAPE_BUTTON_FAILURE,
											errorMessage: (error as Error).message,
										});
										console.error('Failed to launch Seascape:', error);
									}
								}}
							>
								Launch MSI SEASCAPE
							</Button>
						</div>
					</div>
				</div>
				<div className="horizon">
					<div className="innerFrame">
						<div className="msiLogo"></div>
						<div>
							<Button
								size="lg"
								className="horizonButton w-52 items-center"
								onPress={() => {
									try {
										navigateToProduct(
											'Horizon',
											window.config?.HORIZON_APP_URL,
											AppInsightsNavigationReasons.MANUAL_SELECTION_HORIZON,
										);
									} catch (error) {
										trackException(error as Error, LogLevel.Error, {
											userId: loggedInUserInfo?.id || 'unknown',
											emailAddress: loggedInUserInfo?.emailAddress || 'unknown',
											component: AppInsightsComponents.PRODUCT_SELECTION,
											action: AppInsightsActions.HORIZON_BUTTON_FAILURE,
											errorMessage: (error as Error).message,
										});
										console.error('Failed to launch Horizon:', error);
									}
								}}
							>
								Launch MSI HORIZON
							</Button>
						</div>
					</div>
				</div>
			</div>
		);
	} else if (isSubscribedToHorizon || isSubscribedToSeascape) {
		return (
			<div className="flex h-full items-center justify-center">
				<Spinner size="lg" label="Redirecting to your subscribed product..." classNames={{ label: 'text-md' }} variant="simple" />
			</div>
		);
	} else {
		return (
			<div className="flex h-full items-center justify-center">
				<Alert color="warning" className="grow-0 w-auto rounded-none" variant="solid">
					<p className="text-md">You are not subscribed to any product. Please contact MSI.</p>
				</Alert>
			</div>
		);
	}
};

export default ProductSelection;
